import { getCurrencySymbol, NgClass, NgFor, NgIf, NgStyle, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  forwardRef,
  Host,
  Input,
  OnInit,
  Optional,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { FeedProfitCalculations } from 'src/app/feed-profit/services/feed-profit-calculations.service';
import { Discount } from 'src/app/settings/models/settings.model';

import { Product } from '../../../../../shared/models/shared.model';
import {
  Calculation,
  ExtendedSimulation,
  Feeder,
  FeederTableRow,
  FeederType,
  GenderFeedRatio,
  GenderOptions,
  GrowFinisherTypes,
  PriceSetting,
} from '../../../../models/feed-profit.model';
import * as actions from '../../../../store/feed-profit.actions';
import { BaselineCalculatedTableComponent } from '../baseline-calculated-table/baseline-calculated-table.component';
import { BASELINE_FEEDERS_HOST, BASELINE_TABLE_HOST } from '../baseline-feeders.tokens';

@Component({
  selector: 'app-baseline-table',
  templateUrl: './baseline-table.component.html',
  styleUrls: ['./baseline-table.component.scss'],
  imports: [
    TableComponent,
    MatSelect,
    NgClass,
    NgFor,
    MatOption,
    NgIf,
    MatIcon,
    NgStyle,
    BaselineCalculatedTableComponent,
    MatButton,
    AsyncPipe,
    TranslatePipe,
  ],
  providers: [
    { provide: BASELINE_TABLE_HOST, useExisting: forwardRef(() => BaselineTableComponent) },
  ],
})
export class BaselineTableComponent implements OnInit, AfterViewInit {
  @Input() feederType: string;
  @Input() products: Product[];
  @Input() discounts: Discount;
  @Input() feederRows: Feeder[];
  @Input() calculatedEndFeederRows: Feeder[];
  @Input() isExtendedSimulation: boolean;

  productArray = new FormArray([]);
  barrowArray = new FormArray([]);
  boarArray = new FormArray([]);
  giltArray = new FormArray([]);
  averageArray = new FormArray([]);
  percentageArray = new FormArray([]);
  tableConfig: TableConfig;
  tableConfigBase: TableConfig = {
    dataSource: new MatTableDataSource([] as FeederTableRow[]),
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    enableArrowKeys: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };
  private tableColumns: TableColumn[];
  private displayedTableColumns: TableColumn[];
  endFeederType: FeederType = FeederType.END_FEEDER;
  currencySymbol: string;
  productError = false;

  @ViewChild('productNameTemplate') productNameTemplate: TemplateRef<Product>;
  @ViewChild('deleteIconTemplate') deleteIconTemplate: TemplateRef<unknown>;
  @ViewChild(forwardRef(() => BaselineCalculatedTableComponent))
  calculatedTableComponent: BaselineCalculatedTableComponent;
  @Output()
  calculatedFeederPropertiesChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();
  @Output()
  feederPropertiesChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();

  constructor(
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
    @Optional()
    @Host()
    @Inject(BASELINE_FEEDERS_HOST)
    public baselineFeedersComponent: {
      genders?: string;
      calculation?: Calculation;
      products$?: Observable<Product[]>;
      discounts$?: Observable<Discount>;
      tableComponents?: {
        forEach: (callback: (component: BaselineTableComponent) => void) => void;
      };
      calculatedTableComponent?: {
        calculateFeedRatios?: (calculation?: Calculation) => void;
      };
      totalTableTotalAverages?: number;
    },
    private changeDetectorRef: ChangeDetectorRef,
    private calculationsService: FeedProfitCalculations,
  ) {}

  updateData(feederRows: Feeder[], calculatedFeederRows: Feeder[]) {
    const initialDatasource: FeederTableRow[] = [];
    if (feederRows?.length > 0) {
      feederRows?.forEach((row, index) => {
        const datasourceRow = this.getDatasourceRow(row, index);
        this.productArray?.controls[index]?.setValue(datasourceRow.product);
        if (!this.boarArray.controls[index]) {
          this.boarArray.insert(index, new FormControl(datasourceRow.boar));
        }
        if (!this.barrowArray.controls[index]) {
          this.barrowArray.insert(index, new FormControl(datasourceRow.barrow));
        }
        if (!this.giltArray.controls[index]) {
          this.giltArray.insert(index, new FormControl(datasourceRow.gilt));
        }
        if (!this.averageArray.controls[index]) {
          this.averageArray.insert(index, new FormControl(datasourceRow.average));
        }
        if (!this.percentageArray.controls[index]) {
          this.percentageArray.insert(index, new FormControl(datasourceRow.percentage));
        }

        this.boarArray?.controls[index]?.setValue(datasourceRow.boar);
        this.barrowArray?.controls[index]?.setValue(datasourceRow.barrow);
        this.giltArray?.controls[index]?.setValue(datasourceRow.gilt);
        this.averageArray?.controls[index]?.setValue(datasourceRow.average);
        this.percentageArray?.controls[index]?.setValue(datasourceRow.percentage);

        initialDatasource.push(this.getDatasourceRow(row, index));
      });
    } else {
      // empty initial row, because the table should always show at least one row.
      initialDatasource.push({ id: 0 });
    }

    this.displayedTableColumns = this.tableColumns.filter(
      (col) =>
        (this.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT
          ? col.id !== GrowFinisherTypes.BOAR
          : col.id !== GrowFinisherTypes.BARROW) &&
        (this.baselineFeedersComponent?.calculation?.companyDetails?.priceSetting ===
        PriceSetting.NETTO
          ? col.id !== 'priceGross'
          : col.id !== 'priceNet'),
    );

    this.tableConfig = <TableConfig>{
      ...this.tableConfigBase,
      columns: this.displayedTableColumns,
      dataSource: new MatTableDataSource<FeederTableRow>(
        initialDatasource.sort((a, b) => a.id - b.id),
      ),
    };

    if (this.calculatedTableComponent) {
      this.calculatedTableComponent.updateData(calculatedFeederRows);
    }
  }

  ngOnInit(): void {
    this.currencySymbol = getCurrencySymbol(this.localeService.getCustomerCurrency(), 'narrow');
    this.getTableColumns();
    this.updateData(this.feederRows, []);
  }

  ngAfterViewInit() {
    if (this.calculatedTableComponent && this.baselineFeedersComponent) {
      this.baselineFeedersComponent.calculatedTableComponent = this.calculatedTableComponent;
    }
    const newConfig = { ...this.tableConfig };
    if (newConfig.columns) {
      const productColumn = newConfig.columns.find((c) => c.id === 'product');
      productColumn.template = this.productNameTemplate;
      const deleteIconColumn = newConfig.columns.find((c) => c.id === 'deleteIcon');
      deleteIconColumn.template = this.deleteIconTemplate;
      this.tableConfig = newConfig;
    }

    this.tableConfig = newConfig;
    this.updateData(this.feederRows, []);
    this.changeDetectorRef.detectChanges();
  }

  // called from the parent BaselineComponent, when all 3 tables are initialized and available.
  calculatePercentagesOnInit() {
    const newConfig = { ...this.tableConfig };
    // calculate percentages when all three tables are available
    this.tableConfig.dataSource.data.forEach((_row: FeederTableRow, index) => {
      const percentage = this.calculateRowPercentage(index);
      this.percentageArray.controls[index].setValue(percentage ?? 0, { emitEvent: false });
    });
    newConfig.dataSource.data = this.tableConfig.dataSource.data.map(
      (row: FeederTableRow, index) => ({
        ...row,
        percentage: this.calculateRowPercentage(index) ?? 0,
      }),
    );

    this.tableConfig = newConfig;
  }

  private getDatasourceRow(row: Feeder, index: number): FeederTableRow {
    const gilt = row.genderFeedRatios.find((p) => p.name === GenderTypes.GILT)?.feed;
    const barrow = row.genderFeedRatios.find((p) => p.name === GenderTypes.BARROW)?.feed;
    const boar = row.genderFeedRatios.find((p) => p.name === GenderTypes.BOAR)?.feed;

    return {
      id: index,
      product: row.product,
      grossPricePer100Kg: row.product.grossPricePer100Kg,
      unitNettoPrice: this.calculationsService.getProductPriceWithDiscounts(
        row.product.grossPricePer100Kg,
        this.discounts,
      ),
      ewKg: row.product.ewKg,
      lysKg: row.product.lysKg,
      phosphorusGramPerKg: row.product.phosphorusGramPerKg,
      proteinGramPerKg: row.product.proteinGramPerKg,
      gilt,
      barrow,
      boar,
      average: this.calculateAverageGenderFeedRatio(gilt, barrow, boar),
      percentage: this.calculateRowPercentage(index, { gilt, barrow, boar }),
    };
  }

  private getTableColumns() {
    const getControls = (): FormControl[] => {
      const controls: FormControl[] = [];
      this.feederRows?.forEach((_) => controls.push(new FormControl()));
      return controls;
    };
    this.productArray = new FormArray(getControls());
    this.barrowArray = new FormArray(getControls());
    this.boarArray = new FormArray(getControls());
    this.giltArray = new FormArray(getControls());
    this.averageArray = new FormArray(getControls());
    this.percentageArray = new FormArray(getControls());

    this.tableColumns = [
      {
        id: 'product',
        type: TableColumnTypes.TEMPLATE,
        label: this.localeService.translate('feed-profit.detail.baseline-table.product'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.productArray,
        },
      },
      {
        id: 'grossPricePer100Kg',
        type: TableColumnTypes.NUMBER,
        label:
          this.localeService.translate('feed-profit.detail.baseline-table.priceGross') +
          ` (${this.currencySymbol})`,
        disableSort: true,
        digitsInfo: '1.02-2',
      },
      {
        id: 'unitNettoPrice',
        type: TableColumnTypes.NUMBER,
        label:
          this.localeService.translate('feed-profit.detail.baseline-table.priceNet') +
          ` (${this.currencySymbol})`,
        disableSort: true,
        digitsInfo: '1.02-2',
      },
      {
        id: 'ewKg',
        type: TableColumnTypes.NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.energy'),
        disableSort: true,
      },
      {
        id: 'lysKg',
        type: TableColumnTypes.NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.lysine'),
        disableSort: true,
      },
      {
        id: 'phosphorusGramPerKg',
        type: TableColumnTypes.NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.phosphorus'),
        disableSort: true,
      },
      {
        id: 'proteinGramPerKg',
        type: TableColumnTypes.NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.protein'),
        disableSort: true,
      },
      {
        id: GrowFinisherTypes.BARROW,
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.barrow') + '(kg)',
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.barrowArray,
          callback: (row: FeederTableRow, control) => {
            this.executeCallBackWithTimeout(row, control, GrowFinisherTypes.BARROW);
          },
          step: 0.01,
        },
      },
      {
        id: GrowFinisherTypes.BOAR,
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.boar') + '(kg)',
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.boarArray,
          callback: (row: FeederTableRow, control) => {
            this.executeCallBackWithTimeout(row, control, GrowFinisherTypes.BOAR);
          },
          step: 0.01,
        },
      },
      {
        id: GrowFinisherTypes.GILT,
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.gilt') + '(kg)',
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.giltArray,
          callback: (row: FeederTableRow, control) => {
            this.executeCallBackWithTimeout(row, control, GrowFinisherTypes.GILT);
          },
          step: 0.01,
        },
      },
      {
        id: 'average',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.average'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.averageArray,
          decorateFormControl(_row, control) {
            control.disable();
          },
        },
      },
      {
        id: 'percentage',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('feed-profit.detail.baseline-table.percentage'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.percentageArray,
          decorateFormControl(_row, control) {
            control.disable();
          },
        },
      },
      {
        id: 'deleteIcon',
        type: TableColumnTypes.TEMPLATE,
        label: '',
      },
    ];
    this.displayedTableColumns = this.tableColumns.filter(
      (col) =>
        (this.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT
          ? col.id !== GrowFinisherTypes.BOAR
          : col.id !== GrowFinisherTypes.BARROW) &&
        (this.baselineFeedersComponent?.calculation?.companyDetails?.priceSetting ===
        PriceSetting.NETTO
          ? col.id !== 'priceGross'
          : col.id !== 'priceNet'),
    );
  }

  private executeCallBackWithTimeout(
    row: FeederTableRow,
    control: AbstractControl,
    genderType: GrowFinisherTypes,
  ) {
    setTimeout(() => {
      this.calculateAverageAndPercentageColumns(row, control, genderType);
    }, 500);
  }

  // called when barrow/boar/gilt input values is changed
  private calculateAverageAndPercentageColumns(
    tableRow: FeederTableRow,
    _control: AbstractControl,
    _genderType: GrowFinisherTypes,
  ) {
    const gilt: number = this.giltArray?.controls[tableRow.id]?.value;
    const barrow: number = this.barrowArray?.controls[tableRow.id]?.value;
    const boar: number = this.boarArray?.controls[tableRow.id]?.value;

    this.averageArray.controls[tableRow.id].setValue(
      this.calculateAverageGenderFeedRatio(gilt, barrow, boar),
      { emitEvent: false },
    );
    this.percentageArray.controls[tableRow.id].setValue(
      this.calculateRowPercentage(tableRow.id, {
        gilt,
        barrow,
        boar,
      }) ?? 0,
      { emitEvent: false },
    );

    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    this.updateSaveData();

    // calculate percentages in the other two tables
    this.baselineFeedersComponent?.tableComponents?.forEach((component) => {
      if (component.feederType !== this.feederType) {
        component.setOtherTablePercentage();
      }
    });

    this.baselineFeedersComponent?.calculatedTableComponent?.calculateFeedRatios(
      this.baselineFeedersComponent?.calculation,
    );
  }

  private calculateAverageGenderFeedRatio(gilt: number, barrow: number, boar: number): number {
    const secondGender =
      this.baselineFeedersComponent?.genders === GenderOptions.BOAR_GILT ? boar : barrow;

    return ((gilt ?? 0) + (secondGender ?? 0)) / 2;
  }

  private calculateRowPercentage(
    rowIndex: number,
    _values?: { gilt: number; barrow: number; boar: number },
  ): number {
    let currentAverage = 0;
    if (this.baselineFeedersComponent) {
      this.baselineFeedersComponent.totalTableTotalAverages = 0;

      //calculate all normal table component averages
      this.baselineFeedersComponent.tableComponents?.forEach((component) => {
        let tableAveragesSum = 0;
        component?.tableConfig?.dataSource?.data?.map((row: FeederTableRow, index) => {
          const giltRow = component.giltArray?.controls[row.id]?.value;
          const boarRow = component.boarArray?.controls[row.id]?.value;
          const barrowRow = component.barrowArray?.controls[row.id]?.value;

          let secondGender;

          secondGender =
            this.baselineFeedersComponent.genders === GenderOptions.BOAR_GILT
              ? boarRow || 0
              : barrowRow || 0;
          const rowAverage = ((giltRow || 0) + secondGender) / 2;

          secondGender = secondGender && !isNaN(secondGender) ? secondGender : 0;
          tableAveragesSum += rowAverage && !isNaN(rowAverage) ? rowAverage : 0;

          // store the rowAverage of the current row
          if (index === rowIndex && component.feederType === this.feederType) {
            currentAverage = rowAverage && !isNaN(rowAverage) ? rowAverage : 0;
          }
        });
        this.baselineFeedersComponent.totalTableTotalAverages += tableAveragesSum;
      });

      return this.baselineFeedersComponent?.totalTableTotalAverages > 0
        ? Math.round(
            (currentAverage / this.baselineFeedersComponent?.totalTableTotalAverages) * 10000,
          ) / 100
        : 0;
    }
  }

  setOtherTablePercentage() {
    const tableRows = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign({}, row),
    );

    tableRows.forEach((row, index) => {
      this.percentageArray.controls[row.id].setValue(this.calculateRowPercentage(index) ?? 0, {
        emitEvent: false,
      });
    });

    this.updateSaveData();
  }

  changeGenders(updatedGenders: string) {
    if (this.calculatedTableComponent) {
      this.calculatedTableComponent.changeGenders(updatedGenders);
    }
    if (this.baselineFeedersComponent) {
      this.baselineFeedersComponent.genders = updatedGenders;
      this.displayedTableColumns = this.tableColumns.filter(
        (col) =>
          (this.baselineFeedersComponent.genders === GenderOptions.BARROW_GILT
            ? col.id !== GrowFinisherTypes.BOAR
            : col.id !== GrowFinisherTypes.BARROW) &&
          (this.baselineFeedersComponent.calculation?.companyDetails?.priceSetting ===
          PriceSetting.BRUTO
            ? col.id !== 'priceNet'
            : col.id !== 'priceGross'),
      );
    }
    this.tableConfig = <TableConfig>{
      ...this.tableConfig,
      columns: this.displayedTableColumns,
    };
    if (this.feederRows?.length > 0) {
      this.tableConfig.dataSource.data = this.feederRows
        ?.map((row: Feeder, index: number) => ({
          ...this.getDatasourceRow(row, index),
          percentage: this.calculateRowPercentage(index),
        }))
        .sort((a, b) => a.id - b.id);

      this.store.dispatch(actions.setIsChanged({ isChanged: true }));
      this.updateSaveData();
    }
  }

  clearTableRow(selectedRow: FeederTableRow) {
    const feeders: FeederTableRow[] = [];

    this.tableConfig.dataSource.data.forEach((row: FeederTableRow) => {
      if (row.id === selectedRow.id) {
        feeders.push(<FeederTableRow>{});
      } else {
        feeders.push({
          ...row,
          gilt: this.giltArray.controls[row.id].value,
          boar: this.boarArray.controls[row.id].value,
          barrow: this.barrowArray.controls[row.id].value,
        });
      }
    });

    this.tableConfig.dataSource.data = feeders;

    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    this.updateSaveData();
  }

  deleteTableRow(selectedRow: FeederTableRow) {
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data
        .map((row: FeederTableRow) => ({
          ...row,
          gilt: this.giltArray.controls[row.id].value,
          boar: this.boarArray.controls[row.id].value,
          barrow: this.barrowArray.controls[row.id].value,
        }))
        .filter((row: FeederTableRow) => row.id !== selectedRow.id)
        .sort((a: FeederTableRow, b: FeederTableRow) => a.id - b.id),
    ];

    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    this.updateSaveData();
  }

  areProductsEqual(p1: Product, p2: Product): boolean {
    if (!p1 || !p2) {
      return false;
    }
    return p1.id === p2.id && p1.code === p2.code;
  }

  getProduct(row: FeederTableRow, products: Product[]): Product {
    let foundProduct = <Product>{};
    if (row.product) {
      foundProduct = !!products && products.find((product) => product.code === row.product.code);
    }
    return foundProduct;
  }

  selectProduct(selection: MatSelectChange, tableRow: FeederTableRow) {
    const tableRows = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign({}, row),
    );
    const selectedProductIndex = tableRows.findIndex((r) => r.id === tableRow.id);

    this.tableConfig.dataSource.data[selectedProductIndex] = {
      ...tableRows.find((r) => r.id === tableRow.id),
      product: <Product>selection.value,
      grossPricePer100Kg: (<Product>selection.value).grossPricePer100Kg,
      unitNettoPrice: this.calculationsService.getProductPriceWithDiscounts(
        (<Product>selection.value).grossPricePer100Kg,
        this.discounts,
      ),
      ewKg: (<Product>selection.value).ewKg,
      lysKg: (<Product>selection.value).lysKg,
      phosphorusGramPerKg: (<Product>selection.value).phosphorusGramPerKg,
      proteinGramPerKg: (<Product>selection.value).proteinGramPerKg,
    };

    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    this.updateSaveData();
  }

  addProduct(isCalculated = false) {
    let tableRows: FeederTableRow[] = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign(
        {},
        {
          ...row,
          gilt: this.giltArray.controls[row.id].value,
          barrow: this.barrowArray.controls[row.id].value,
          boar: this.boarArray.controls[row.id].value,
        },
      ),
    );
    const index = Math.max(...tableRows.map((row) => row.id), 0) + 1;
    tableRows = [
      ...tableRows,
      <FeederTableRow>{
        id: index,
        product: {},
        isCalculated,
      },
    ];
    this.productArray.insert(index, new FormControl());
    this.barrowArray.insert(index, new FormControl());
    this.boarArray.insert(index, new FormControl());
    this.giltArray.insert(index, new FormControl());
    this.averageArray.insert(index, new FormControl(0));
    this.percentageArray.insert(index, new FormControl(0));

    this.tableConfig.dataSource.data = tableRows;

    this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    this.updateSaveData();
  }

  // update the selected calculation in the state with the data from this table,
  // so that the data can be saved from the component higher up (feed-profit-item.component)
  updateSaveData() {
    const values = <Calculation>{
      ...this.baselineFeedersComponent?.calculation,
    };
    const feeders: Feeder[] = this.isExtendedSimulation
      ? (this.baselineFeedersComponent?.calculation?.extendedSimulation?.feeders?.filter(
          (f) => f.type !== this.feederType,
        ) ?? [])
      : (this.baselineFeedersComponent?.calculation?.feeders?.filter(
          (f) => f.type !== this.feederType,
        ) ?? []);
    this.tableConfig.dataSource.filteredData.forEach((row: FeederTableRow, index: number) => {
      const genderFeedRatios = <GenderFeedRatio[]>[];

      genderFeedRatios.push(<GenderFeedRatio>{
        name: GenderTypes.GILT,
        feed: this.giltArray?.controls[index]?.value,
      });

      if (this.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT) {
        genderFeedRatios.push(<GenderFeedRatio>{
          name: GenderTypes.BARROW,
          feed: this.barrowArray?.controls[index]?.value,
        });
      } else {
        genderFeedRatios.push(<GenderFeedRatio>{
          name: GenderTypes.BOAR,
          feed: this.boarArray?.controls[index]?.value,
        });
      }
      const feeder = <Feeder>{
        id: index,
        type: this.feederType,
        product: <Product>{
          id: row.product?.id,
          code: row.product?.code,
          name: row.product?.name,
          grossPricePer100Kg: row.grossPricePer100Kg,
          unitNettoPrice: row.unitNettoPrice,
          ewKg: row.ewKg,
          lysKg: row.lysKg,
          phosphorusGramPerKg: row.phosphorusGramPerKg,
          proteinGramPerKg: row.proteinGramPerKg,
          suitablePhases: [],
          energySystem: null,
          lysineSystem: null,
          schemeTypes: [],
        },
        genderFeedRatios,
      };
      feeders.push(feeder);
    });
    if (this.isExtendedSimulation) {
      values.extendedSimulation = <ExtendedSimulation>{
        ...this.baselineFeedersComponent?.calculation?.extendedSimulation,
        feeders,
      };
    } else {
      values.feeders = feeders;
    }
    this.feederPropertiesChanged.emit(values);
  }

  calculatedFeederPropertiesChangedPropegation($event: Calculation) {
    this.calculatedFeederPropertiesChanged.emit($event);
  }
}
